import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "next-i18next";

import { Card } from "@/components/ui/Card";
import { Heading } from "common/ui/Heading";
import { Input } from "common/ui/Input";
import { Button } from "common/ui/Button";
import { Stack } from "common/ui/Stack";
import { ErrorDisplay } from "common/ui/ErrorDisplay";
import { Paragraph } from "common/ui/Paragraph";

import { forgotPassword } from "common/middleware/api.auth";
import { forgotPassValidation } from "common/middleware/validation";

import styles from "./ForgotPasswordForm.module.scss";

export function ForgotPasswordForm() {
  const [error, setError] = useState("");

  const { query } = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setValue,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(forgotPassValidation),
  });
  const { t } = useTranslation("auth");

  const submitForgotPassword = async (values: any) => {
    const result = await forgotPassword(values.email);

    if (!result) {
      setError(t("somethingWentWrong") as string);
    }
  };

  useEffect(() => {
    if (query.email) {
      return setValue("email", query.email as any);
    }
  }, [query]);

  return (
    <Card className={styles.ForgotPasswordForm}>
      <Heading variant="h2" className={styles.TextColor}>
        {t("forgot.forgotPassword")}
      </Heading>
      <form onSubmit={handleSubmit(submitForgotPassword)}>
        <Stack direction="column" gap={24}>
          <Input
            type="email"
            label="Email"
            errors={errors}
            disabled={query.email !== undefined}
            light
            {...register("email")}
          />
          <Stack direction="column" gap={4}>
            <Button
              variant="primary"
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitSuccessful ? t("forgot.resendEmail") : t("forgot.getResetCode")}
            </Button>
            {error && <ErrorDisplay error={error} />}
          </Stack>
          {isSubmitSuccessful && (
            <Paragraph variant="p2" className={styles.TextColor}>
              {t("forgot.hasBeenSent")}
            </Paragraph>
          )}
        </Stack>
      </form>
    </Card>
  )
}
